// Rounded QR geometry shared by the on-screen SVG and the download canvas:
// one module matrix, drawn as soft dots with rounded finder rings.

import qrcodegen from '@helpers/qrcodegen';

const ECC = {
    L: qrcodegen.QrCode.Ecc.LOW,
    M: qrcodegen.QrCode.Ecc.MEDIUM,
    Q: qrcodegen.QrCode.Ecc.QUARTILE,
    H: qrcodegen.QrCode.Ecc.HIGH,
};

// Dot geometry in module units.
const DOT = 0.84;      // dot side (the rest is breathing room)
const DOT_RADIUS = 0.3;
const FINDER = 7;      // finder pattern side

/**
 * Encodes `text` and clears a centered square of `excavate` modules (for the
 * logo mark). Returns { size, modules(boolean[][]), finders([{x,y}]) }.
 */
export function buildQr(text, level = 'M', excavate = 0) {
    const code = qrcodegen.QrCode.encodeText(String(text || ' '), ECC[level] || ECC.M);
    const size = code.size;
    const modules = code.getModules().map(row => row.slice());

    if (excavate > 0) {
        const start = Math.floor((size - excavate) / 2);
        for (let y = start; y < start + excavate; y++) {
            for (let x = start; x < start + excavate; x++) {
                if (modules[y]) modules[y][x] = false;
            }
        }
    }

    const finders = [
        { x: 0, y: 0 },
        { x: size - FINDER, y: 0 },
        { x: 0, y: size - FINDER },
    ];

    return { size, modules, finders };
}

function inFinder(x, y, finders) {
    return finders.some(f => x >= f.x && x < f.x + FINDER && y >= f.y && y < f.y + FINDER);
}

// Every dark module outside the finder patterns, as {x, y}.
export function dataDots(qr) {
    const dots = [];
    for (let y = 0; y < qr.size; y++) {
        for (let x = 0; x < qr.size; x++) {
            if (qr.modules[y][x] && !inFinder(x, y, qr.finders)) dots.push({ x, y });
        }
    }
    return dots;
}

// SVG path for a rounded rect (module units).
function roundedRectPath(x, y, w, h, r) {
    const rr = Math.min(r, w / 2, h / 2);
    return [
        `M${x + rr},${y}`,
        `h${w - 2 * rr}`,
        `a${rr},${rr} 0 0 1 ${rr},${rr}`,
        `v${h - 2 * rr}`,
        `a${rr},${rr} 0 0 1 ${-rr},${rr}`,
        `h${-(w - 2 * rr)}`,
        `a${rr},${rr} 0 0 1 ${-rr},${-rr}`,
        `v${-(h - 2 * rr)}`,
        `a${rr},${rr} 0 0 1 ${rr},${-rr}`,
        'z',
    ].join('');
}

/**
 * The whole code as SVG path data in module units: rounded dots for data, and
 * for each finder a rounded ring (7×7 minus 5×5, even-odd) plus a rounded 3×3
 * center. Paint it with `fill-rule="evenodd"`.
 */
export function qrPath(qr) {
    const parts = [];
    const inset = (1 - DOT) / 2;

    dataDots(qr).forEach(({ x, y }) => {
        parts.push(roundedRectPath(x + inset, y + inset, DOT, DOT, DOT_RADIUS));
    });

    qr.finders.forEach(({ x, y }) => {
        parts.push(roundedRectPath(x, y, 7, 7, 2.1));
        parts.push(roundedRectPath(x + 1, y + 1, 5, 5, 1.4));
        parts.push(roundedRectPath(x + 2, y + 2, 3, 3, 0.9));
    });

    return parts.join('');
}

// Canvas twin of qrPath: draws the code in `fillStyle` inside a px×px box.
export function drawQr(ctx, qr, px, fillStyle) {
    const unit = px / qr.size;
    const inset = (1 - DOT) / 2;

    const rrect = (x, y, w, h, r) => {
        const rr = Math.min(r, w / 2, h / 2);
        ctx.moveTo(x + rr, y);
        ctx.arcTo(x + w, y, x + w, y + h, rr);
        ctx.arcTo(x + w, y + h, x, y + h, rr);
        ctx.arcTo(x, y + h, x, y, rr);
        ctx.arcTo(x, y, x + w, y, rr);
        ctx.closePath();
    };

    ctx.save();
    ctx.scale(unit, unit);
    ctx.fillStyle = fillStyle;

    ctx.beginPath();
    dataDots(qr).forEach(({ x, y }) => rrect(x + inset, y + inset, DOT, DOT, DOT_RADIUS));
    ctx.fill();

    qr.finders.forEach(({ x, y }) => {
        ctx.beginPath();
        rrect(x, y, 7, 7, 2.1);
        rrect(x + 1, y + 1, 5, 5, 1.4);
        ctx.fill('evenodd');
        ctx.beginPath();
        rrect(x + 2, y + 2, 3, 3, 0.9);
        ctx.fill();
    });

    ctx.restore();
}
